// SPDX-License-Identifier: MIT
// Compose a full SAS URL from raw input (URL or query) with validation and sanitization.

import { validateAzureConfig, AzureRawEnvConfig } from '../../utils/config-validation';
import { sanitizeSasInput, hardenUrlEcho } from '../../utils/sanitization';
import { normalizeInput } from './input';

// Regexes reused for defense-in-depth re-validation (align with config-validation.ts)
const ACCOUNT_RE = /^[a-z0-9-]{3,63}$/;
const CONTAINER_RE = /^[a-z0-9-]{3,63}$/;
// Allow either blob or dfs endpoints
const HOST_RE = /^https:\/\/[a-z0-9-]{3,63}\.(blob|dfs)\.core\.windows\.net\//;

export interface BuildUrlResult {
  url: string;
  error?: string;
  sanitizationIssues?: string[];
}

export function buildFullSasUrl(
  rawInput: string,
  overrides?: Partial<AzureRawEnvConfig>
): BuildUrlResult {
  const norm = normalizeInput(rawInput);
  const { value: sanitized, issues } = sanitizeSasInput(norm);
  if (!sanitized) return { url: '', error: 'Empty SAS input' };
  // Reject any explicit non-https scheme early, but only when a URL scheme is present at the very start.
  // Note: SAS query strings can legitimately contain colons (e.g., sv=2023-11-03), so we must not
  // misclassify those as schemes. We only consider a scheme if it matches RFC pattern at the beginning.
  const schemeMatch = sanitized.match(/^[A-Za-z][A-Za-z0-9+.-]*:/);
  if (schemeMatch) {
    const scheme = schemeMatch[0].slice(0, -1).toLowerCase(); // drop trailing ':'
    if (scheme !== 'https') {
      return {
        url: '',
        error: 'SCHEME_REJECTED',
        sanitizationIssues: [...issues, 'SCHEME_REJECTED'],
      };
    }
  }
  if (sanitized.startsWith('https://')) {
    // Host allow‑list validation (defense-in-depth)
    try {
      const hardened = hardenUrlEcho(sanitized);
      if (!HOST_RE.test(hardened)) {
        return {
          url: '',
          error: 'Host validation failed – expected <acct>.blob.core.windows.net/',
          sanitizationIssues: [...issues, 'INVALID_HOST'],
        };
      }
      // Re-validate account/container derivation from URL path when possible
      const u = new URL(hardened);
      const pathFirst = u.pathname.split('/').filter(Boolean)[0];
      if (!pathFirst) {
        // Container must be present in path for container SAS
        return {
          url: '',
          error: 'Missing container segment in SAS URL',
          sanitizationIssues: [...issues, 'MISSING_CONTAINER'],
        };
      }
      if (!CONTAINER_RE.test(pathFirst)) {
        // Access Vite env safely with Node fallback (tests may mutate process.env rather than immutable import.meta.env)
        const viteFlag = (import.meta as unknown as { env?: Record<string, string | undefined> })
          .env?.VITE_AZURE_STRICT_CONTAINER;
        const nodeFlag =
          typeof process !== 'undefined'
            ? (process as any).env?.VITE_AZURE_STRICT_CONTAINER
            : undefined;
        const strictContainer = (viteFlag || nodeFlag) === 'true';
        if (strictContainer) {
          return {
            url: '',
            error: 'Invalid container (strict)',
            sanitizationIssues: [...issues, 'INVALID_CONTAINER'],
          };
        }
        return { url: hardened, sanitizationIssues: [...issues, 'INVALID_CONTAINER_WEAK'] };
      }
      return { url: hardened, sanitizationIssues: issues };
    } catch {
      return {
        url: '',
        error: 'Malformed SAS URL',
        sanitizationIssues: [...issues, 'PARSE_ERROR'],
      };
    }
  }
  // assume raw query token -> need env account/container
  const { config } = validateAzureConfig(overrides as any);
  // If token appears to be directory-scoped (sr=d), require a full URL with a directory path so
  // we can compute the correct dfs/container context. Building a container URL from env would fail auth.
  if (/([?&])sr=d([&#]|$)/i.test(sanitized)) {
    return {
      url: '',
      error:
        'Directory-scoped SAS detected: please paste the full SAS URL including the directory path',
      sanitizationIssues: [...issues, 'DIR_SAS_NEEDS_URL'],
    };
  }
  const forceFallback =
    (import.meta as any)?.env?.VITE_AZURE_TEST_FORCE_FALLBACK === '1' ||
    (typeof process !== 'undefined' &&
      (process as any).env?.VITE_AZURE_TEST_FORCE_FALLBACK === '1');
  const missing = !config.accountName || !config.containerName;
  if (missing || forceFallback) {
    // If explicit force flag set, always return deterministic fallback (test-only behaviour gated by dedicated flag).
    if (forceFallback) {
      return {
        url: `https://testacct.blob.core.windows.net/testcontainer?${sanitized}`,
        sanitizationIssues: [...issues, 'TEST_FORCED_FALLBACK'],
      };
    }
    // Otherwise only fallback in NODE_ENV==='test'; production remains strict.
    const nodeEnv = typeof process !== 'undefined' ? (process as any).env?.NODE_ENV : undefined;
    if (missing && nodeEnv === 'test') {
      return {
        url: `https://testacct.blob.core.windows.net/testcontainer?${sanitized}`,
        sanitizationIssues: [...issues, 'TEST_FALLBACK_ENV'],
      };
    }
    if (missing) {
      return {
        url: '',
        error: 'Account or container env vars missing for raw SAS token',
        sanitizationIssues: issues,
      };
    }
  }
  // Re-validate env values (defense-in-depth) – should already be validated, but fail closed if tampered.
  if (!config.accountName || !ACCOUNT_RE.test(config.accountName)) {
    return {
      url: '',
      error: 'Account name failed validation regex',
      sanitizationIssues: [...issues, 'INVALID_ACCOUNT'],
    };
  }
  if (!config.containerName || !CONTAINER_RE.test(config.containerName)) {
    return {
      url: '',
      error: 'Container name failed validation regex',
      sanitizationIssues: [...issues, 'INVALID_CONTAINER'],
    };
  }
  return {
    url: `https://${config.accountName}.blob.core.windows.net/${config.containerName}?${sanitized}`,
    sanitizationIssues: issues,
  };
}
